import { useCallback, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Page,
  Card,
  TextField,
  Button,
  Badge,
  DataTable,
  BlockStack,
  InlineStack,
  Text,
  Banner,
  Modal,
  FormLayout,
  Select,
  ChoiceList,
  EmptyState,
  SkeletonBodyText,
} from '@shopify/polaris';
import SearchableSelect from '@/components/SearchableSelect';
import { clinicsApi } from '@/services/clinics';
import { patientsApi } from '@/services/patients';
import {
  miscShipmentsApi,
  type MiscAddress,
  type MiscDirection,
  type MiscShipment,
} from '@/services/miscShipments';
import type { Clinic, Patient, RateQuote } from '@/types';

const PAGE_SIZE = 50;

const DIRECTION_OPTIONS = [
  { label: 'Send to customer', value: 'outbound' },
  { label: 'Customer sends back to us', value: 'return' },
];

const EMPTY_ADDRESS: MiscAddress = {
  name: '',
  company: '',
  addressLine1: '',
  addressLine2: '',
  city: '',
  stateProvince: '',
  postalCode: '',
  countryCode: 'CA',
  phone: '',
  email: '',
};

function errorMessage(err: unknown): string {
  const e = err as { response?: { data?: { message?: string | string[] } }; message?: string };
  const m = e?.response?.data?.message;
  if (Array.isArray(m)) return m.join('; ');
  return m || e?.message || 'Something went wrong';
}

function money(v: string | number | null | undefined): string {
  return v == null || v === '' ? '—' : `$${Number(v).toFixed(2)}`;
}

function rateKey(r: RateQuote): string {
  return `${r.provider ?? ''}|${r.carrierCode}|${r.serviceCode}`;
}

/** Clinic.shippingAddress is Shopify-shaped (address1/zip/province/country_code). */
function addressFromClinic(c: Clinic): MiscAddress {
  const a = (c.shippingAddress || {}) as Record<string, string | undefined>;
  return {
    ...EMPTY_ADDRESS,
    name: c.name,
    addressLine1: a.address1 || '',
    addressLine2: a.address2 || '',
    city: a.city || '',
    stateProvince: a.province || '',
    postalCode: a.zip || '',
    countryCode: a.country_code || 'CA',
    phone: c.phone || '',
    email: c.email || '',
  };
}

function addressFromPatient(p: Patient): MiscAddress {
  return {
    ...EMPTY_ADDRESS,
    name: `${p.firstName} ${p.lastName}`.trim(),
    addressLine1: p.streetAddress || '',
    city: p.city || '',
    stateProvince: p.province || '',
    postalCode: p.postalCode || '',
    countryCode: p.country || 'CA',
    phone: p.phone || '',
    email: p.email || '',
  };
}

interface NewShipmentModalProps {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
}

function NewShipmentModal({ open, onClose, onCreated }: NewShipmentModalProps) {
  const [direction, setDirection] = useState<MiscDirection>('outbound');
  const [address, setAddress] = useState<MiscAddress>(EMPTY_ADDRESS);
  const [contents, setContents] = useState('');
  const [comment, setComment] = useState('');
  const [charge, setCharge] = useState('');
  const [weight, setWeight] = useState('2');

  const [clinicId, setClinicId] = useState('');
  const [clinicName, setClinicName] = useState('');
  const [clinicCache, setClinicCache] = useState<Clinic[]>([]);
  const [patientId, setPatientId] = useState('');
  const [patientName, setPatientName] = useState('');
  const [patientCache, setPatientCache] = useState<Patient[]>([]);

  const [rates, setRates] = useState<RateQuote[] | null>(null);
  const [selectedRate, setSelectedRate] = useState('');
  const [error, setError] = useState('');
  const [created, setCreated] = useState<MiscShipment | null>(null);

  const searchClinics = useCallback(async (query: string) => {
    const res = await clinicsApi.list({ search: query || undefined, limit: 25, status: 'active' });
    setClinicCache(res.data);
    return res.data.map((c) => ({ label: c.name, value: c.id, detail: c.email || undefined }));
  }, []);

  const searchPatients = useCallback(async (query: string) => {
    const res = await patientsApi.search(query, undefined, 25);
    setPatientCache(res);
    return res.map((p) => ({
      label: `${p.firstName} ${p.lastName}`,
      value: p.id,
      detail: p.email || p.phone || undefined,
    }));
  }, []);

  // Any change to what was quoted invalidates the quotes.
  const invalidateRates = () => {
    setRates(null);
    setSelectedRate('');
  };

  const setField = (key: keyof MiscAddress) => (value: string) => {
    setAddress((a) => ({ ...a, [key]: value }));
    invalidateRates();
  };

  const reset = () => {
    setDirection('outbound');
    setAddress(EMPTY_ADDRESS);
    setContents('');
    setComment('');
    setCharge('');
    setWeight('2');
    setClinicId('');
    setClinicName('');
    setPatientId('');
    setPatientName('');
    setRates(null);
    setSelectedRate('');
    setError('');
    setCreated(null);
  };

  const close = () => {
    const wasCreated = !!created;
    reset();
    onClose();
    if (wasCreated) onCreated();
  };

  const weightNum = Number(weight);
  const packages = [{ weight: weightNum > 0 ? weightNum : 2 }];

  const ratesMutation = useMutation({
    mutationFn: () => miscShipmentsApi.getRates({ direction, address: cleanAddress(address), packages }),
    onSuccess: (data) => {
      setRates(data);
      setError(data.length === 0 ? 'No rates came back for this address and weight.' : '');
      const cheapest = [...data].sort((a, b) => a.totalCost - b.totalCost)[0];
      setSelectedRate(cheapest ? rateKey(cheapest) : '');
    },
    onError: (err) => setError(errorMessage(err)),
  });

  const createMutation = useMutation({
    mutationFn: () => {
      const rate = rates?.find((r) => rateKey(r) === selectedRate);
      return miscShipmentsApi.create({
        direction,
        address: cleanAddress(address),
        packages,
        contents: contents.trim(),
        comment: comment.trim(),
        chargeAmount: charge.trim() ? Number(charge) : undefined,
        clinicId: clinicId || undefined,
        patientId: patientId || undefined,
        provider: rate?.provider,
        carrierCode: rate?.carrierCode,
        serviceCode: rate?.serviceCode,
        serviceName: rate?.serviceName,
      });
    },
    onSuccess: (data) => {
      setCreated(data);
      setError('');
    },
    onError: (err) => setError(errorMessage(err)),
  });

  const formComplete =
    address.name.trim() &&
    address.addressLine1.trim() &&
    address.city.trim() &&
    address.stateProvince.trim() &&
    address.postalCode.trim() &&
    address.countryCode.trim() &&
    address.phone.trim() &&
    weightNum > 0;
  const chargeValid = !charge.trim() || (Number(charge) >= 0 && !Number.isNaN(Number(charge)));
  const canCreate = !!rates && !!selectedRate && contents.trim() && comment.trim() && chargeValid;

  const primaryAction = created
    ? { content: 'Done', onAction: close }
    : rates && rates.length > 0
      ? {
          content: 'Create label',
          onAction: () => createMutation.mutate(),
          loading: createMutation.isPending,
          disabled: !canCreate,
        }
      : {
          content: 'Get rates',
          onAction: () => ratesMutation.mutate(),
          loading: ratesMutation.isPending,
          disabled: !formComplete,
        };

  return (
    <Modal
      open={open}
      onClose={close}
      title="New Misc. Shipment"
      primaryAction={primaryAction}
      secondaryActions={created ? [] : [{ content: 'Cancel', onAction: close }]}
    >
      <Modal.Section>
        {created ? (
          <BlockStack gap="300">
            <Banner tone="success" title="Label created">
              <p>Tracking number: {created.trackingNumber}</p>
            </Banner>
            <InlineStack>
              <Button onClick={() => miscShipmentsApi.openLabel(created.id)}>Download / print label</Button>
            </InlineStack>
          </BlockStack>
        ) : (
          <FormLayout>
            {error && (
              <Banner tone="critical">
                <p>{error}</p>
              </Banner>
            )}
            <Select
              label="Direction"
              options={DIRECTION_OPTIONS}
              value={direction}
              onChange={(v) => {
                setDirection(v as MiscDirection);
                invalidateRates();
              }}
              helpText={
                direction === 'return'
                  ? 'Prepaid label the customer uses to send something to us. Address below is where it ships from.'
                  : 'Label for us to send something to the address below.'
              }
            />

            <SearchableSelect
              label="Clinic (optional, prefills the address)"
              value={clinicId}
              selectedLabel={clinicName}
              onChange={(v, option) => {
                setClinicId(v);
                setClinicName(option?.label ?? '');
                const c = clinicCache.find((x) => x.id === v);
                if (c) {
                  setAddress(addressFromClinic(c));
                  setPatientId('');
                  setPatientName('');
                  invalidateRates();
                }
              }}
              onSearch={searchClinics}
              emptyOption={{ label: 'No clinic', value: '' }}
              placeholder="Type a clinic name…"
            />
            <SearchableSelect
              label="Patient (optional, prefills the address)"
              value={patientId}
              selectedLabel={patientName}
              onChange={(v, option) => {
                setPatientId(v);
                setPatientName(option?.label ?? '');
                const p = patientCache.find((x) => x.id === v);
                if (p) {
                  setAddress(addressFromPatient(p));
                  setClinicId('');
                  setClinicName('');
                  invalidateRates();
                }
              }}
              onSearch={searchPatients}
              emptyOption={{ label: 'No patient', value: '' }}
              minChars={2}
              placeholder="Type a patient name…"
            />

            <FormLayout.Group>
              <TextField label="Name" autoComplete="off" value={address.name} onChange={setField('name')} />
              <TextField label="Company" autoComplete="off" value={address.company ?? ''} onChange={setField('company')} />
            </FormLayout.Group>
            <TextField label="Address" autoComplete="off" value={address.addressLine1} onChange={setField('addressLine1')} />
            <TextField label="Address line 2" autoComplete="off" value={address.addressLine2 ?? ''} onChange={setField('addressLine2')} />
            <FormLayout.Group>
              <TextField label="City" autoComplete="off" value={address.city} onChange={setField('city')} />
              <TextField label="Province / state" autoComplete="off" value={address.stateProvince} onChange={setField('stateProvince')} />
            </FormLayout.Group>
            <FormLayout.Group>
              <TextField label="Postal code" autoComplete="off" value={address.postalCode} onChange={setField('postalCode')} />
              <TextField label="Country code" autoComplete="off" maxLength={2} value={address.countryCode} onChange={setField('countryCode')} />
            </FormLayout.Group>
            <FormLayout.Group>
              <TextField label="Phone" autoComplete="off" value={address.phone} onChange={setField('phone')} />
              <TextField label="Email" autoComplete="off" value={address.email ?? ''} onChange={setField('email')} />
            </FormLayout.Group>

            <FormLayout.Group>
              <TextField
                label="Package weight (lbs)"
                type="number"
                autoComplete="off"
                value={weight}
                onChange={(v) => {
                  setWeight(v);
                  invalidateRates();
                }}
              />
              <TextField
                label="Charge to customer (optional)"
                type="number"
                prefix="$"
                autoComplete="off"
                value={charge}
                onChange={setCharge}
                helpText="Informational: what the customer is billed."
                error={chargeValid ? undefined : 'Enter a valid amount'}
              />
            </FormLayout.Group>
            <TextField
              label="Contents"
              autoComplete="off"
              value={contents}
              onChange={setContents}
              placeholder="e.g. Brochures, Wrap return"
              maxLength={200}
            />
            <TextField
              label="Comment"
              autoComplete="off"
              multiline={3}
              value={comment}
              onChange={setComment}
              helpText="Why this label was made. Stays on file with the tracking number."
            />

            {rates && rates.length > 0 && (
              <ChoiceList
                title="Choose a service"
                selected={[selectedRate]}
                onChange={(v) => setSelectedRate(v[0])}
                choices={[...rates]
                  .sort((a, b) => a.totalCost - b.totalCost)
                  .map((r) => ({
                    label: `${r.carrierName} ${r.serviceName} — $${r.totalCost.toFixed(2)} ${r.currency}${
                      r.estimatedDays ? ` (${r.estimatedDays} days)` : ''
                    }`,
                    value: rateKey(r),
                  }))}
              />
            )}
          </FormLayout>
        )}
      </Modal.Section>
    </Modal>
  );
}

/** Drop empty optional strings so the API sees them as absent. */
function cleanAddress(a: MiscAddress): MiscAddress {
  const out: MiscAddress = { ...a };
  (['company', 'addressLine2', 'email'] as const).forEach((k) => {
    if (!out[k]?.trim()) delete out[k];
  });
  out.countryCode = out.countryCode.trim().toUpperCase();
  return out;
}

export default function MiscShipments() {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [submittedSearch, setSubmittedSearch] = useState('');
  const [page, setPage] = useState(0);
  const [modalOpen, setModalOpen] = useState(false);
  const [error, setError] = useState('');

  const { data, isLoading } = useQuery({
    queryKey: ['misc-shipments', submittedSearch, page],
    queryFn: () =>
      miscShipmentsApi.list({
        search: submittedSearch || undefined,
        skip: page * PAGE_SIZE,
        take: PAGE_SIZE,
      }),
  });

  const voidMutation = useMutation({
    mutationFn: (id: string) => miscShipmentsApi.void(id),
    onSuccess: (res) => {
      setError(res.success ? '' : `Carrier did not void the label: ${res.message}`);
      queryClient.invalidateQueries({ queryKey: ['misc-shipments'] });
    },
    onError: (err) => setError(errorMessage(err)),
  });

  const openLabel = async (id: string) => {
    try {
      await miscShipmentsApi.openLabel(id);
    } catch (err) {
      setError(errorMessage(err));
    }
  };

  const rows = (data?.data ?? []).map((s) => [
    new Date(s.createdAt).toLocaleDateString(),
    <BlockStack key={`r-${s.id}`}>
      <Text as="span">{s.address.name}</Text>
      <Text as="span" tone="subdued" variant="bodySm">
        {s.direction === 'return' ? 'Return to us' : 'Outbound'}
        {s.address.company ? ` · ${s.address.company}` : ''}
      </Text>
    </BlockStack>,
    s.contents,
    s.comment,
    s.trackingNumber || '—',
    money(s.chargeAmount),
    <Badge key={`b-${s.id}`} tone={s.status === 'voided' ? 'critical' : 'success'}>
      {s.status === 'voided' ? 'Voided' : 'Label created'}
    </Badge>,
    <InlineStack key={`a-${s.id}`} gap="200" wrap={false}>
      {s.hasLabel && s.status !== 'voided' && (
        <Button variant="plain" onClick={() => openLabel(s.id)}>
          Label
        </Button>
      )}
      {s.status !== 'voided' && (
        <Button
          variant="plain"
          tone="critical"
          loading={voidMutation.isPending && voidMutation.variables === s.id}
          onClick={() => {
            if (window.confirm(`Void the label ${s.trackingNumber || ''}? The carrier label will be cancelled.`)) {
              voidMutation.mutate(s.id);
            }
          }}
        >
          Void
        </Button>
      )}
    </InlineStack>,
  ]);

  const total = data?.total ?? 0;

  return (
    <Page
      title="Misc. Shipments"
      subtitle="Labels that are not tied to a rental: brochures out, a wrap back"
      primaryAction={{ content: 'New Misc. Shipment', onAction: () => setModalOpen(true) }}
    >
      <BlockStack gap="400">
        {error && (
          <Banner tone="critical" onDismiss={() => setError('')}>
            <p>{error}</p>
          </Banner>
        )}
        <Card>
          <BlockStack gap="300">
            <TextField
              label="Search"
              labelHidden
              autoComplete="off"
              placeholder="Search name, tracking number, comment or contents"
              value={search}
              onChange={setSearch}
              clearButton
              onClearButtonClick={() => {
                setSearch('');
                setSubmittedSearch('');
                setPage(0);
              }}
              connectedRight={
                <Button
                  onClick={() => {
                    setSubmittedSearch(search.trim());
                    setPage(0);
                  }}
                >
                  Search
                </Button>
              }
            />
            {isLoading ? (
              <SkeletonBodyText lines={6} />
            ) : rows.length === 0 ? (
              <EmptyState heading="No misc. shipments" image="">
                <p>Create one to generate a label that is not tied to a rental.</p>
              </EmptyState>
            ) : (
              <DataTable
                columnContentTypes={['text', 'text', 'text', 'text', 'text', 'numeric', 'text', 'text']}
                headings={['Date', 'Recipient', 'Contents', 'Comment', 'Tracking', 'Charge', 'Status', '']}
                rows={rows}
              />
            )}
            {total > PAGE_SIZE && (
              <InlineStack align="space-between" blockAlign="center">
                <Text as="span" tone="subdued">
                  {page * PAGE_SIZE + 1}-{Math.min((page + 1) * PAGE_SIZE, total)} of {total}
                </Text>
                <InlineStack gap="200">
                  <Button disabled={page === 0} onClick={() => setPage((p) => p - 1)}>
                    Previous
                  </Button>
                  <Button disabled={(page + 1) * PAGE_SIZE >= total} onClick={() => setPage((p) => p + 1)}>
                    Next
                  </Button>
                </InlineStack>
              </InlineStack>
            )}
          </BlockStack>
        </Card>
      </BlockStack>

      {modalOpen && (
        <NewShipmentModal
          open
          onClose={() => setModalOpen(false)}
          onCreated={() => queryClient.invalidateQueries({ queryKey: ['misc-shipments'] })}
        />
      )}
    </Page>
  );
}
